import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';

interface CreditMemoRow {
  id: string;
  number: string;
  customer_id: string;
  entry_date: string;
  reason: string;
  currency: string;
  grand_total: string;
  applied_amount: string;
  status: string;
}

const STATUS_CLASSES: Record<string, string> = {
  open: 'bg-yellow-100 text-yellow-700',
  void: 'bg-slate-100 text-slate-500',
};

export function CreditMemos() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bk-credit-memos', activeOrgId],
    queryFn: () =>
      api<{ credit_memos: CreditMemoRow[] }>('/bookkeeping/sales/credit-memos?limit=100'),
    enabled: !!activeOrgId,
  });
  const creditMemos = data?.credit_memos ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('sales.creditMemos.title')}</h1>
        <Link
          to="/app/bookkeeping/sales/credit-memos/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('sales.creditMemos.new')}
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('sales.creditMemos.fields.number')}</th>
                <th className="px-3 py-2">{t('sales.creditMemos.fields.customer')}</th>
                <th className="px-3 py-2">{t('sales.creditMemos.fields.entry_date')}</th>
                <th className="px-3 py-2">{t('sales.creditMemos.fields.reason')}</th>
                <th className="px-3 py-2">{t('sales.creditMemos.fields.currency')}</th>
                <th className="px-3 py-2 text-right">
                  {t('sales.creditMemos.fields.grand_total')}
                </th>
                <th className="px-3 py-2 text-right">
                  {t('sales.creditMemos.fields.applied_amount')}
                </th>
                <th className="px-3 py-2">{t('sales.creditMemos.fields.status')}</th>
              </tr>
            </thead>
            <tbody>
              {creditMemos.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-slate-400">
                    {t('sales.creditMemos.empty')}
                  </td>
                </tr>
              )}
              {creditMemos.map((cm) => (
                <tr key={cm.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/sales/credit-memos/${cm.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {cm.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">
                    {cm.customer_id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-2">{cm.entry_date}</td>
                  <td className="px-3 py-2">
                    {t(`sales.creditMemos.reasons.${cm.reason}`, { defaultValue: cm.reason })}
                  </td>
                  <td className="px-3 py-2">{cm.currency}</td>
                  <td className="px-3 py-2 text-right font-mono">{cm.grand_total}</td>
                  <td className="px-3 py-2 text-right font-mono">{cm.applied_amount}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-xs ${STATUS_CLASSES[cm.status] ?? ''}`}
                    >
                      {t(`sales.creditMemos.statuses.${cm.status}`, { defaultValue: cm.status })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
